import React, { useEffect, useState } from "react";
import Loading from "../Common/Loading";
import { useNavigate } from "react-router-dom";

interface ProgressRow {
  yr: number;
  previousScore: number;
  latestScore: number;
  scoreDiff: number;
  trend: "up" | "down" | "stable";
  highlighted: boolean;
}

interface LessonPracticeRow {
  date: string;
  activity: string;
  score: number;
  time: string;
  attempt: number;
  chapter: string;
  unit: string;
  level: string;
  selectedFrom: string;
}

interface Summary {
  daysGap: number;
  previousDate: string;
  latestDate: string;
  previousTestTime: string;
  latestTestTime: string;
  practiceTime: string;
  prevStudyYear: number;
  latestStudyYear: number;
  previousSkipText: string;
  latestSkipText: string;
}

interface ReportResponse {
  found: boolean;
  message?: string;
  dealer?: string;
  familyCode?: string;
  child?: string;
  summary?: Summary;
  progressRows?: ProgressRow[];
  lessonPracticeRows?: LessonPracticeRow[];
}

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

const MDAnalysisReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<ReportResponse | null>(null);

  useEffect(() => {
    document.body.style.backgroundColor = '#fff'
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const schoolCode =
      params.get("S") ||
      params.get("SchoolCode") ||
      "";

    const familyCode =
      params.get("F") ||
      params.get("FamilyCode") ||
      params.get("familycode") ||
      "";

    const login =
      params.get("L") ||
      params.get("Login") ||
      "";
    (async () => {
      setLoading(true);
      setError("");
      try {
        const query = new URLSearchParams({
          school_code: schoolCode,
          family_code: familyCode,
          login,
        });
        const resp = await fetch(`${API_BASE}api/reports/md-analysis-report/?${query.toString()}`, {
          credentials: "include",
        });
        if (!resp.ok) throw new Error(`Request failed with status ${resp.status}`);
        const json: ReportResponse = await resp.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load report");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // const handleClose = () => {
  //   navigate(-1);
  // };

  const handleClose = () => {
    // If this page was opened using window.open(),
    // close the actual browser window.
    if (window.opener && !window.opener.closed) {
      window.close();

      // Some browsers may prevent closing in certain cases.
      // Only fall back to navigation if the window is still open.
      setTimeout(() => {
        if (!window.closed) {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/Support");
          }
        }
      }, 100);

      return;
    }

    // If opened directly (not as a popup), navigate normally.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/SysAdmin");
    }
  };


  const summary = data?.summary;
  const progressRows = data?.progressRows ?? [];
  const lessonRows = data?.lessonPracticeRows ?? [];
  const lessonHeaderText =
    data?.found && lessonRows.length === 0
      ? "MD-MD Lesson Practice - No records found"
      : "MD-MD Lesson Practice";

  return (
    <div className="mdar-report">
      <div className="mdar-title-bar">MD ANALYSIS REPORT</div>

      <div className="mdar-filter-row">
        <div className="mdar-filter-fields">
          <span className="mdar-label">Dealer:</span>
          <span className="mdar-value">{data?.dealer ?? ""}</span>
          <span className="mdar-label">Login:</span>
          <span className="mdar-value">{data?.familyCode ?? ""}</span>
          <span className="mdar-label">Child:</span>
          <span className="mdar-value">{data?.child ?? ""}</span>
        </div>
        <button type="button" className="mdar-btn" onClick={handleClose}>
          CLOSE
        </button>
      </div>

      {loading && (
        <div className="mdar-loading-wrap">
          <Loading />
        </div>
      )}

      {!loading && error && <div className="mdar-alert mdar-alert-danger">{error}</div>}

      {!loading && !error && data && !data.found && (
        <div className="mdar-nodata">{data.message || "No records found"}</div>
      )}

      {!loading && !error && data && data.found && summary && (
        <>
          <div className="mdar-summary">
            <b>Summary:</b>
            <span className="mdar-summary-item">Days gap: {summary.daysGap}</span>
            <span className="mdar-summary-item">
              Previous Date: <b>{summary.previousDate}</b>
            </span>
            <span className="mdar-summary-item">
              Latest Date: <b>{summary.latestDate}</b>
            </span>
            <span className="mdar-summary-item" style={{ color: "#8e44ad" }}>
              Previous Time: <b>{summary.previousTestTime}</b>
            </span>
            <span className="mdar-summary-item" style={{ color: "#16a085" }}>
              Latest Time: <b>{summary.latestTestTime}</b>
            </span>
            <span className="mdar-summary-item" style={{ color: "orange" }}>
              Practice Time: <b>{summary.practiceTime}</b>
            </span>
            <span className="mdar-summary-item" style={{ color: "blue" }}>
              Study Year:{" "}
              <b>
                {summary.prevStudyYear === summary.latestStudyYear
                  ? summary.prevStudyYear
                  : `${summary.prevStudyYear} → ${summary.latestStudyYear}`}
              </b>
            </span>
            <span className="mdar-summary-item">
              Previous AnsSkipped: <b>{summary.previousSkipText}</b>
            </span>
            <span className="mdar-summary-item">
              Latest AnsSkipped: <b>{summary.latestSkipText}</b>
            </span>
          </div>

          <div className="mdar-tables-row">
            <div className="mdar-lesson-container">
              <div className="mdar-lesson-title">MD Progress Summary</div>
              <table className="mdar-lesson-table">
                <thead>
                  <tr className="mdar-lesson-header">
                    <th>Year</th>
                    <th>Previous Score</th>
                    <th>Latest Score</th>
                    <th>Score Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {progressRows.map((row) => (
                    <tr
                      key={row.yr}
                      className="mdar-lesson-row"
                      style={row.highlighted ? { backgroundColor: "#ffe599", fontWeight: "bold" } : undefined}
                    >
                      <td>{row.yr}</td>
                      <td>{Number(row.previousScore).toFixed(2)}</td>
                      <td>{Number(row.latestScore).toFixed(2)}</td>
                      <td
                        style={{
                          color: row.trend === "up" ? "green" : row.trend === "down" ? "red" : "black",
                        }}
                      >
                        {row.trend === "up" && `▲ ${row.scoreDiff}`}
                        {row.trend === "down" && `▼ ${row.scoreDiff}`}
                        {row.trend === "stable" && "Stable"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mdar-lesson-container">
              <div className="mdar-lesson-title">{lessonHeaderText}</div>
              {lessonRows.length > 0 && (
                <table className="mdar-lesson-table">
                  <thead>
                    <tr className="mdar-lesson-header">
                      <th>Date</th>
                      <th>Activity</th>
                      <th>Score</th>
                      <th>Time</th>
                      <th>Attempt</th>
                      <th>Chapter</th>
                      <th>Unit</th>
                      <th>Level</th>
                      <th>Selected From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessonRows.map((row, idx) => (
                      <tr key={idx} className="mdar-lesson-row">
                        <td>{row.date}</td>
                        <td>{row.activity}</td>
                        <td>{row.score}</td>
                        <td>{row.time}</td>
                        <td>{row.attempt}</td>
                        <td>{row.chapter}</td>
                        <td>{row.unit}</td>
                        <td>{row.level}</td>
                        <td>{row.selectedFrom}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== IN-FILE CSS — no Bootstrap or any external stylesheet required ===== */}
      <style>{`
        .mdar-report {
          font-family: Arial, Helvetica, sans-serif;
          padding: 10px 16px;
          box-sizing: border-box;
        }
        .mdar-report *, .mdar-report *::before, .mdar-report *::after {
          box-sizing: border-box;
        }

        .mdar-title-bar {
          text-align: center;
          font-size: 16pt;
          font-weight: bold;
          color: #fff;
          background-color: teal;
          padding: 8px;
          margin-bottom: 10px;
        }

        .mdar-filter-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 10px;
        }
        .mdar-filter-fields {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          font-size: 16px;
          font-weight: bold;
        }
        .mdar-label {
          color: #222;
        }
        .mdar-value {
          color: blue;
          margin-right: 14px;
        }

        .mdar-btn {
          height: 30px;
          padding: 0 16px;
          font-family: Arial;
          font-size: 14pt;
          border: 1px solid #ced4da;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
        }

        .mdar-loading-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }

        .mdar-alert {
          margin: 12px 0;
          padding: 10px 16px;
          border-radius: 8px;
          text-align: center;
          font-size: 14px;
        }
        .mdar-alert-danger {
          background: #f8d7da;
          color: #842029;
          border: 1px solid #f5c2c7;
        }

        .mdar-nodata {
          padding: 20px;
          text-align: center;
          font-size: 14px;
          color: #555;
        }

        .mdar-summary {
          background: #eef3ff;
          border: 1px solid #5c84c3;
          padding: 10px;
          margin-bottom: 10px;
          font-size: 14px;
        }
        .mdar-summary-item {
          margin-left: 20px;
        }

        .mdar-tables-row {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .mdar-lesson-container {
          margin-top: 10px;
          font-size: 16px;
        }

        .mdar-lesson-title {
          text-align: center;
          font-weight: bold;
          font-size: 16px;
          padding: 6px;
          background-color: #b8cce4;
          border: 1px solid #5c84c3;
          border-bottom: none;
          color: #1f3a6d;
        }

        .mdar-lesson-table {
          border-collapse: collapse;
          table-layout: auto;
        }
        .mdar-lesson-header th {
          background-color: #b8cce4;
          color: #1f3a6d;
          border: 1px solid #5c84c3;
          padding: 5px;
          font-weight: bold;
          height: 25px;
          text-align: left;
          white-space: nowrap;
        }
.mdar-lesson-row td {
  border: 1px solid #5c84c3;
  padding: 5px;
  height: 25px;
  font-size: 14px;
  white-space: nowrap;
  background-color: #edf2fb;
}
  .mdar-lesson-row[style*="background-color"] td {
  background-color: #ffe599 !important;
  font-weight: bold;
}
        .mdar-lesson-row:hover td {
          background-color: #9dc3e6;
        }
      `}</style>
    </div>
  );
};

export default MDAnalysisReport;